import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Loader from '../../components/Loader';
import { NumericFormatCustom } from '../../components/NumericCustomFormatter';
import { Settings } from '../../client/services';

export const FOLLOW_UP_CODES_SETTING_NAME = 'follow_up_codes';

type PromoKey = 'loyalty' | 'welcome' | 'winback';

type PromoEntry = {
  code: string;
  validDays: number;
  discountPercent: number;
};

export type FollowUpCodesFormValues = Record<PromoKey, PromoEntry>;

export const DEFAULT_FOLLOW_UP_CODES_FORM: FollowUpCodesFormValues = {
  loyalty: { code: 'LOYAL15', validDays: 30, discountPercent: 15 },
  welcome: { code: 'WELCOME15', validDays: 15, discountPercent: 15 },
  winback: { code: 'MISSYOU20', validDays: 15, discountPercent: 20 },
};

const PROMO_LABELS: Record<PromoKey, string> = {
  loyalty: 'Loyalty (1.3)',
  welcome: 'Welcome (2.3)',
  winback: 'Winback (4.2)',
};

export const parseFollowUpCodesValue = (
  raw: unknown
): FollowUpCodesFormValues => {
  let parsed: any = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { ...DEFAULT_FOLLOW_UP_CODES_FORM };
    }
  }
  if (!parsed || typeof parsed !== 'object') {
    return { ...DEFAULT_FOLLOW_UP_CODES_FORM };
  }

  const normalize = (key: PromoKey): PromoEntry => {
    const fallback = DEFAULT_FOLLOW_UP_CODES_FORM[key];
    const entry = parsed[key] || {};
    const days = Number(entry.validDays);
    const pct = Number(
      entry.discountPercent ?? entry.discountAmount ?? entry.discountPct
    );
    return {
      code: String(entry.code || fallback.code)
        .trim()
        .toUpperCase(),
      validDays:
        Number.isFinite(days) && days > 0 ? Math.floor(days) : fallback.validDays,
      discountPercent:
        Number.isFinite(pct) && pct > 0
          ? Math.floor(pct)
          : fallback.discountPercent,
    };
  };

  return {
    loyalty: normalize('loyalty'),
    welcome: normalize('welcome'),
    winback: normalize('winback'),
  };
};

const FollowUpCodesSettingsPage = () => {
  const [settingId, setSettingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [values, setValues] = useState<FollowUpCodesFormValues>({
    ...DEFAULT_FOLLOW_UP_CODES_FORM,
  });
  const [savedValues, setSavedValues] = useState<FollowUpCodesFormValues>({
    ...DEFAULT_FOLLOW_UP_CODES_FORM,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const response: any = await Settings.find({
          query: {
            search: FOLLOW_UP_CODES_SETTING_NAME,
            limit: 10,
            skip: 0,
          },
        });
        const setting =
          (response?.data || []).find(
            (item: any) => item.name === FOLLOW_UP_CODES_SETTING_NAME
          ) || null;
        if (!cancelled && setting) {
          const parsed = parseFollowUpCodesValue(setting.value);
          setSettingId(setting.id);
          setValues(parsed);
          setSavedValues(parsed);
        }
      } catch (error: any) {
        toast.error(error?.message || 'Failed to load Follow up Codes');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCodeChange =
    (key: PromoKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({
        ...prev,
        [key]: { ...prev[key], code: event.target.value.toUpperCase() },
      }));
    };

  const handleNumberChange =
    (key: PromoKey, field: 'validDays' | 'discountPercent') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = Number(event.target.value);
      const next =
        Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
      setValues((prev) => ({
        ...prev,
        [key]: { ...prev[key], [field]: next },
      }));
    };

  const handleCancel = () => {
    setValues({ ...savedValues });
  };

  const validateBeforeSave = () => {
    for (const key of Object.keys(PROMO_LABELS) as PromoKey[]) {
      const entry = values[key];
      if (!String(entry.code || '').trim()) {
        toast.error(`${PROMO_LABELS[key]}: code is required`);
        return false;
      }
      if (!entry.validDays || entry.validDays <= 0) {
        toast.error(`${PROMO_LABELS[key]}: valid days must be > 0`);
        return false;
      }
      if (!entry.discountPercent || entry.discountPercent <= 0) {
        toast.error(`${PROMO_LABELS[key]}: discount % must be > 0`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) {
      return;
    }

    setIsSaving(true);
    try {
      const sanitized: FollowUpCodesFormValues = {
        loyalty: {
          code: values.loyalty.code.trim().toUpperCase(),
          validDays: Math.max(1, values.loyalty.validDays),
          discountPercent: Math.max(1, values.loyalty.discountPercent),
        },
        welcome: {
          code: values.welcome.code.trim().toUpperCase(),
          validDays: Math.max(1, values.welcome.validDays),
          discountPercent: Math.max(1, values.welcome.discountPercent),
        },
        winback: {
          code: values.winback.code.trim().toUpperCase(),
          validDays: Math.max(1, values.winback.validDays),
          discountPercent: Math.max(1, values.winback.discountPercent),
        },
      };

      const payload = {
        name: FOLLOW_UP_CODES_SETTING_NAME,
        value: JSON.stringify(sanitized),
        valueType: 'json',
        language: '',
        activeState: true,
      };

      if (settingId) {
        await Settings.patch(settingId, payload);
      } else {
        const created: any = await Settings.create(payload);
        setSettingId(created?.id || null);
      }

      setValues(sanitized);
      setSavedValues(sanitized);
      toast.success('Follow up Codes saved');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save Follow up Codes');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Page title='Follow up Codes'>
      <Container maxWidth='lg'>
        <HeaderBreadcrumbs
          heading='Follow up Codes'
          links={[
            { name: 'Dashboard', href: '/dashboard' },
            { name: 'Follow up Codes' },
          ]}
        />

        <Card sx={{ p: 3 }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Promo code, discount %, and validity days for loyalty, welcome, and
            winback follow-ups.
          </Typography>

          <Stack spacing={3}>
            {(Object.keys(PROMO_LABELS) as PromoKey[]).map((key, index) => (
              <Box key={key}>
                {index > 0 && <Divider sx={{ mb: 3 }} />}
                <Typography variant='subtitle1' sx={{ mb: 2 }}>
                  {PROMO_LABELS[key]}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label='Code'
                      value={values[key].code}
                      onChange={handleCodeChange(key)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label='Discount %'
                      value={values[key].discountPercent}
                      onChange={handleNumberChange(key, 'discountPercent')}
                      InputProps={{
                        inputComponent: NumericFormatCustom as any,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label='Valid days'
                      value={values[key].validDays}
                      onChange={handleNumberChange(key, 'validDays')}
                      InputProps={{
                        inputComponent: NumericFormatCustom as any,
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Stack>

          <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mt: 4 }}>
            <LoadingButton variant='outlined' onClick={handleCancel} disabled={isSaving}>
              Cancel
            </LoadingButton>
            <LoadingButton
              variant='contained'
              loading={isSaving}
              onClick={handleSave}
            >
              Save
            </LoadingButton>
          </Stack>
        </Card>
      </Container>
    </Page>
  );
};

export default FollowUpCodesSettingsPage;
